import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole:
    | 'super_admin'
    | 'admin'
    | 'candidate'
    | 'invigilator'
    | Array<'super_admin' | 'admin' | 'candidate' | 'invigilator'>;
}

function hasRoleAccess(userRole: string, requiredRole: string): boolean {
  if (userRole === requiredRole) {
    return true;
  }

  if (requiredRole === 'admin') {
    return userRole === 'super_admin';
  }

  return false;
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user || !profile) {
    return (
      <Navigate
        to="/error/401"
        replace
        state={{
          from: `${location.pathname}${location.search}`,
          message: 'You need to sign in to continue.'
        }}
      />
    );
  }

  if (profile.password_changed === false && location.pathname !== '/force-password-change') {
    return <Navigate to="/force-password-change" replace />;
  }

  const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  if (!allowedRoles.some((role) => hasRoleAccess(profile.role, role))) {
    return (
      <Navigate
        to="/error/403"
        replace
        state={{
          from: `${location.pathname}${location.search}`,
          message: 'Your account does not have access to this area.'
        }}
      />
    );
  }

  return <>{children}</>;
}
